import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';

import { AuthService } from './auth.service';
import { EMPTY, Observable } from 'rxjs';
import { SnackbarService } from '@nrwl/snackbar-services';
import { SnackbarType } from '@nrwl/rx-models';
import { WebviewService } from '@nrwl/rx-webview-service';
import { catchError } from 'rxjs/operators';
import { environment } from '@nrwl/environment';
import { isPresignedStorageUrl } from './presigned-url.util';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  env = environment;
  snackbarType = SnackbarType;
  private noSkip401 = true;
  private authService: AuthService | null = null;

  constructor(
    private snackbarService: SnackbarService,
    private injector: Injector,
    private webviewService: WebviewService,
  ) {}

  private getAuthService(): AuthService {
    if (!this.authService) {
      this.authService = this.injector.get(AuthService);
    }
    return this.authService;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Redirect incomplete routes to Angular 10
    // const redirectPaths = [
    //     '/v19/administration/anatomy',
    //     '/v19/administration/client-record/dashboard/forms',
    //     '/v19/galleries',
    //     '/v19/leads',
    //     '/v19/inbox',
    //     '/v19/administration/account-settings/dashboard/team',
    //     '/v19/user-setting/dashboard/profile',
    //     '/v19/clients/notification',
    // ];
    // const currentHref = window.location.href;
    // const shouldRedirect = redirectPaths.some(path => currentHref.includes(path));
    // if (shouldRedirect) {
    //     const newPath = currentHref.replace('/v19/', '/');
    //     window.location.href = newPath;
    //     return EMPTY;
    // }

    if (req.body && req.body['x-skip-interceptor']) {
      const modifiedBody = { ...req.body };
      delete modifiedBody['x-skip-interceptor'];

      const modifiedReq = req.clone({
        body: modifiedBody,
      });

      return next.handle(modifiedReq).pipe(
        catchError((error: any) => {
          return this.errorHandler(error.status, error);
        }),
      );
    }

    let authToken = this.getAuthService().getToken();

    if (authToken && !isPresignedStorageUrl(req.url)) {
      authToken = authToken.replace(/"/g, '');
      const headers = {};

      headers['Authorization'] = `Token ${authToken}`;
      if (!req.headers.has('Accept')) {
        headers['Accept'] = `application/json`;
      }
      req = req.clone({
        url: req.url.replace(/\+/gi, '%2B'),
      });
      req = req.clone({ setHeaders: headers });
    }

    return next.handle(req).pipe(
      catchError((error: any) => {
        // not need to clear storage on each error request (we have 404 for example)
        // if(authToken && cookieToken){
        //     this._auth.clearStorage();
        // }

        const suppress403All = req.headers;

        const suppress403 = req.headers.get('Suppress-403') === 'true';
        if (error.status === 403 && suppress403) {
          // Suppress the error handling
          return EMPTY;
        }

        return this.errorHandler(error.status, error);
      }),
    );
  }

  private isUnauthorizedError() {
    this.noSkip401 = false;
    setTimeout(() => (this.noSkip401 = true), 3000);
  }

  private errorHandler(status, response: HttpErrorResponse): Observable<HttpEvent<any>> {
    switch (status) {
      // unauthorized error
      case 400:
        {
          if (response.error?.response === 'Task has not finished yet') {
            break;
          }
          if (response?.error?.non_field_errors) {
            this.snackbarService.notify(
              response.error.non_field_errors[0],
              this.snackbarType.error,
            );
          } else {
            this.snackbarService.notify(this.getErrorMessage(response), this.snackbarType.error);
          }
        }
        break;
      case 401:
        {
          if (this.noSkip401) {
            this.isUnauthorizedError();
            if (window.location.origin === this.env.rxAuth) {
              this.snackbarService.notify(this.getErrorMessage(response), this.snackbarType.error);
            } else if (window.location.origin === this.env.rxForms) {
              const message = 'ERROR 401: Unathorized';
              this.snackbarService.notify(message, this.snackbarType.error);
            } else if (window.location.origin === this.env.rxPortal) {
              const message = 'An error occured. Please try again later.';
              this.snackbarService.notify(message, SnackbarType.error);
              this.webviewService.logout();
              this.getAuthService().logout(true);
            } else {
              this.getAuthService().logout(true);
            }
          }
        }
        break;
      // no permissions
      case 403:
        {
          const message = this.getErrorMessage(response);
          this.snackbarService.notify(message, this.snackbarType.error);
        }
        break;

      // default behaviour
      default: {
        const message = 'Connection error, please, try again later.';
        //this.snackbarService.notify(message, this.snackbarType.error);
      }
    }
    throw response;
  }

  private getErrorMessage(response: HttpErrorResponse): string {
    const error = response?.error;

    if (typeof error === 'string' && error.trim()) {
      return error;
    }

    if (error && typeof error === 'object') {
      const detail = (error as { detail?: unknown }).detail;

      if (typeof detail === 'string' && detail.trim()) {
        return detail;
      }

      const nonFieldErrors = (error as { non_field_errors?: unknown }).non_field_errors;

      if (Array.isArray(nonFieldErrors)) {
        const message = nonFieldErrors.find(
          (value): value is string => typeof value === 'string' && Boolean(value.trim()),
        );

        if (message) {
          return message;
        }
      }

      for (const [field, value] of Object.entries(error)) {
        const message = Array.isArray(value)
          ? value.find((item): item is string => typeof item === 'string' && Boolean(item.trim()))
          : typeof value === 'string' && value.trim()
            ? value
            : null;

        if (message) {
          return `${field}: ${message}`;
        }
      }
    }

    return 'Something went wrong. Please try again.';
  }
}
